import React, { useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import {
  Box,
  Divider,
  IconButton,
  InputBase,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Chat, ExpandLess, ExpandMore } from '@mui/icons-material';
import { timeSince } from '@par/shared/utils/date-time';
import { ChatMessage } from '../room/types';
import { StyledPaperItem } from '../../common/mui-components';
import { DrawerButton, ExpandIcon } from '.';
import { isMobile } from 'react-device-detect';

type PropTypes = {
  allChat?: ChatMessage[];
  handleMinimizeAllChat: () => void;
  open: boolean;
  socket: Socket;
  username: string;
};

export const AllChat = ({
  allChat,
  handleMinimizeAllChat,
  open,
  socket,
  username,
}: PropTypes) => {
  const theme = useTheme();
  const xsBreakpoint = useMediaQuery(theme.breakpoints.only('xs'));

  const [inputHeight, setInputHeight] = useState(56);
  const [showTimestamp, setShowTimestamp] = useState(false);
  const inputBaseRef = useRef<any>(null);
  const boxRef = useRef<any>(null);

  // Because of multiline input we need to dynamically adjust the height of the messages box area
  useEffect(() => {
    if (inputBaseRef.current) {
      setInputHeight(
        inputBaseRef.current.clientHeight === 0
          ? 56
          : inputBaseRef.current.clientHeight
      );
    }
    scrollToBottomOfChat();
  }, []);

  useEffect(() => {
    const keyDownHandler = (event: any) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (!inputBaseRef.current?.value) return;
        socket.emit('messageAllChat', {
          username,
          message: inputBaseRef.current.value,
        });
        inputBaseRef.current.value = '';
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, []);

  useEffect(() => {
    if (open) {
      focusInputBase();
      if (isMobile) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    }
  }, [open]);

  useEffect(() => {
    scrollToBottomOfChat();
  }, [allChat]);

  const handleInput = () => {
    if (inputBaseRef.current) {
      setInputHeight(inputBaseRef.current.clientHeight);
    }
    scrollToBottomOfChat();
  };

  const scrollToBottomOfChat = () => {
    const scrollHeight = boxRef.current.scrollHeight;
    boxRef.current.scrollTop = scrollHeight;
  };

  const focusInputBase = () => inputBaseRef.current.focus();

  return (
    <>
      <DrawerButton sx={{ width: '100%' }} onClick={handleMinimizeAllChat}>
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={2}
          width={'100%'}
        >
          <Chat />
          <Typography>All Chat</Typography>
          <ExpandIcon aria-label="expand">
            {open ? <ExpandMore /> : <ExpandLess />}
          </ExpandIcon>
        </Stack>
      </DrawerButton>
      <Box
        overflow={'auto'}
        width={'100%'}
        padding={1}
        height={
          xsBreakpoint
            ? `calc(100% - (88px + ${inputHeight}px))`
            : `calc(400px - ${inputHeight}px - 88px)`
        }
        ref={boxRef}
        className="CHAT_BOX"
        onClick={focusInputBase}
        onMouseEnter={() => setShowTimestamp(true)}
        onMouseLeave={() => setShowTimestamp(false)}
      >
        {allChat?.map((msg) => (
          <Stack
            key={msg.createdAt}
            direction={'row'}
            alignItems={'center'}
            sx={{ position: 'relative' }}
          >
            <Typography
              ml={1}
              color="primary.main"
              textAlign={'left'}
              width={'100%'}
            >
              <Tooltip placement="top" title={msg.username}>
                <span
                  style={{
                    marginRight: 10,
                    textAlign: 'left',
                    color: 'white',
                    minWidth: '50px',
                    maxWidth: '120px',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'pre',
                  }}
                >
                  {msg.username}:
                </span>
              </Tooltip>
              {msg.message}
            </Typography>
            {showTimestamp ? (
              <Typography
                fontStyle={'italic'}
                fontSize="16px"
                paddingX={1}
                sx={{
                  color: 'tertiary.main',
                  backgroundColor: 'rgb(33, 34, 37, 0.8)',
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  borderRadius: 2,
                }}
              >
                {timeSince(new Date(msg.createdAt || 0))}
              </Typography>
            ) : null}
          </Stack>
        ))}
      </Box>

      <Box position={'absolute'} bottom={0} width={'100%'}>
        <Divider />

        <StyledPaperItem
          sx={{
            width: 'calc(100% - 16px)',
            margin: 1,
          }}
        >
          <InputBase
            multiline
            maxRows={4}
            type="text"
            inputRef={inputBaseRef}
            onInput={handleInput}
            sx={{
              // ml: 1,
              '.css-156xqnd-MuiInputBase-input': { padding: 0 },
            }}
            fullWidth
            placeholder="Type something..."
          />
        </StyledPaperItem>
      </Box>
    </>
  );
};
